import { Injectable, ComponentFactoryResolver, ApplicationRef, Injector, EmbeddedViewRef, ComponentRef, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, Observable, Subscription, switchMap, take, tap, throwError } from 'rxjs';
import { VersionToggleService } from './version-toggle';
import { ErrorDailogBoxComponent } from '../shared/components/error-dailog-box/error-dailog-box.component';
import { ErrorDetail, ErrorResponse, RequestBody } from '../shared/models/request-body.interface';
import { ErrorType } from '../models/error.model';
import { AuthUiService } from './auth-ui.service';
import { ErrorService } from './error.service';
import { AppFacadeService } from './app-facade.service';
import { AppMediatorService } from './app-mediator.service';
import { LoggerService } from './logger.service';
import { TranslateService } from '@ngx-translate/core';
import { ApiResultMessage, ApiResultMessageType } from '@por/shared/core';

@Injectable()
export class BackendInterceptor implements HttpInterceptor {
    private readonly versionToggle = inject(VersionToggleService);
    private readonly componentFactoryResolver = inject(ComponentFactoryResolver);
    private readonly appRef = inject(ApplicationRef);
    private readonly injector = inject(Injector);
    private readonly errorService = inject(ErrorService);
    private readonly auth = inject(AuthUiService);
    private readonly appfacade = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly logger = inject(LoggerService);
    private readonly translateService = inject(TranslateService);

    private currentDialogRef: ComponentRef<ErrorDailogBoxComponent> | null = null;

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return this.appfacade.getIsAdmin().pipe(
            take(1), // Get the latest value and complete
            switchMap(isAdmin => {
                return next.handle(request).pipe(
                    tap(event => {
                        if (event instanceof HttpResponse) {
                            const bodyMessage: ApiResultMessage = event.body?.Message ? event.body.Message : null;
                            if (bodyMessage && bodyMessage.Type === ApiResultMessageType.Error) {
                                this.handleError(
                                    {
                                        status: bodyMessage.Status ? bodyMessage.Status : event.status,
                                        url: event.url || '',
                                        error: {
                                            message: bodyMessage.Message ? bodyMessage.Message : event.statusText
                                        }
                                    },
                                    request,
                                    isAdmin
                                );
                            }
                        }
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.handleError(
                            {
                                status: error?.status ?? error?.error?.statusCode ?? 0,
                                url: request?.url || '',
                                error: {
                                    message: error?.error?.message ?? this.translateService.instant('DefaultErrorMessage'),
                                    token: error?.error?.token
                                }
                            },
                            request,
                            isAdmin
                        );
                        this.appfacade.setLoading(false);
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    private createDialogConfig(body: RequestBody, errorStatus: string): ErrorDetail {
        return {
            errormessage: 'An error occurred while processing your request',
            quickLink: this.versionToggle.isQuickLinkVersion() ? window.location.href : '',
            orgId: body?.OrganizationId || this.appMediatorService.localStorageService.getCurrentUser?.organizationId || '',
            customerId: body?.CustomerId || this.appMediatorService.localStorageService.getCurrentUser?.customerId || '',
            date: new Date().toString(),
            contractId: body?.ContractId || this.appMediatorService.localStorageService.getCurrentUser?.contractId || '',
            errorStatus: errorStatus || ''
        };
    }

    private showDialog(config: ErrorDetail, allowClose = false): void {
        if (this.currentDialogRef) {
            this.cleanupDialog(this.currentDialogRef);
            this.currentDialogRef = null;
        }

        // Create the component manually
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ErrorDailogBoxComponent);
        const componentRef = componentFactory.create(this.injector);
        this.currentDialogRef = componentRef;

        componentRef.instance.data = config;
        componentRef.instance.allowClose = allowClose;

        // Use object destructuring to set properties
        Object.assign(componentRef.instance, config);

        // Attach the component to the application
        this.appRef.attachView(componentRef.hostView);

        // Append the component to the body
        const domElem = (componentRef.hostView as EmbeddedViewRef<ErrorDailogBoxComponent>).rootNodes[0] as HTMLElement;
        document.body.appendChild(domElem);

        // Subscribe to retry action
        const retrySubscription = componentRef.instance.retry.subscribe(() => {
            this.reload(); // Reload the window on Retry
        });

        // Subscribe to closeDialog action
        const closeSubscription = componentRef.instance.closeDialog.subscribe(() => {
            this.cleanupDialog(componentRef, retrySubscription, closeSubscription);
            this.currentDialogRef = null;
        });

        // Ensure all subscriptions are unsubscribed when dialog is destroyed
        const cleanupCallback = () => {
            this.cleanupDialog(componentRef, retrySubscription, closeSubscription);
            this.currentDialogRef = null;
        };

        retrySubscription.add(cleanupCallback);
        closeSubscription.add(cleanupCallback);
    }

    private reload(): void {
        window.location.reload();
    }

    private cleanupDialog(componentRef: ComponentRef<ErrorDailogBoxComponent>, ...subscriptions: Subscription[]): void {
        // Detach and destroy the component
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();

        // Unsubscribe to prevent memory leaks
        subscriptions.forEach(sub => sub.unsubscribe());
    }

    private handleError(error: ErrorResponse, request: HttpRequest<unknown>, isAdmin: boolean) {
        let dialogConfig: ErrorDetail = this.createDialogConfig(request.body as RequestBody, error.status.toString());
        /**
         * if online, but error.status === 0, mean GAPI is timeout, consider it as 502
         */
        if (navigator.onLine && error.status === 0) {
            error = {
                ...error,
                status: 502
            };
        }
        const errorMessage = this.errorHandlingAlerts(error.status, error.error.message);
        dialogConfig = {
            ...dialogConfig,
            errorStatus: error.status.toString(),
            errormessage: errorMessage,
            apiMessage: error.error.message
        };
        const isNetworkError = error.status === 0;
        const isOffline = !navigator.onLine;

        /**
         * First check for Offline, then check for api connectivity.
         */
        if (isOffline) {
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('ApplicationOffline')
            };
            this.showDialog(dialogConfig);
        } else if (isNetworkError) {
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('NetworkError')
            };
            this.showDialog(dialogConfig);
        }
        const isInternalError = [500, 404].includes(error.status);
        const isBadRequestError = [400].includes(error.status);
        const isRateLimited = error.status === 429;
        const isRefreshTokenError = error?.url?.includes('tokens/refresh');
        const isGoogleMapError = error?.url?.includes('maps.googleapis.com');

        /**
         * Check for SQS message, because when There is no expert server tied to your organization
         * GAPI returns message with 403 status code
         */
        const isSQSError = [502, 504].includes(error.status) || error.error.message.includes('SQS');
        // The AI image dialog fires 4 parallel generate requests and handles partial failures
        // itself (busy slots retire silently; an all-failed message shows only when every request
        // settles empty) — a global gateway-timeout popup per failed request would be noise.
        const isAiImageGenerate = request.url.includes('ai-image/generate');
        // The eSign create can outlast the gateway timeout; the contract detail shows its own
        // "create stalled" message, so the global gateway dialog (whose Retry reloads the page) is suppressed.
        const isEsignRequest = request.url.includes('esign-request');
        const is503 = error.status === 503;
        const excludeUrls: string[] = [
            'getpdf',
            'REST/v1/Locations',
            'create-transaction',
            'livelink',
            'sms/send',
            // Public org config has its own error handling (InvalidSubDomainOrgId).
            // Onboarding endpoint has its own error handling in admin component.
            'public/organizations/config',
            'apikey/onboard/setup'
        ];

        if (isAdmin) {
            excludeUrls.push('public/organizations/url');
            excludeUrls.push('external-credentials/hapn/test');
        }
        const isExcludedUrl: boolean = excludeUrls.some(url => request.url.includes(url));

        // Routes that manage their own 429 UX (e.g. ORG_SMS_CAP_EXCEEDED on utility/contracts/.../send)
        // and would otherwise show this generic dialog on top of their own targeted message. Kept
        // separate from excludeUrls, which also gates the 401/403 auto-logout branch below plus the
        // 400/404/500 dialogs — excluding a route from the rate-limit dialog must not silently disable
        // session-expiry handling or other error dialogs for it too.
        const selfHandled429Urls: string[] = ['utility/contracts'];
        const isSelfHandled429Url: boolean = selfHandled429Urls.some(url => request.url.includes(url));

        // ContractEffects returns the consumer to their quote on this rejection; the generic dialog would
        // only add the raw message key. Token mirrors api-lib ErrorTokens.QuoteStepsIncomplete.
        const isQuoteStepsIncomplete = request.url.includes('convert-to-reservation') && error.error?.token === 'QUOTE_STEPS_INCOMPLETE';

        // The send-payments-to-rms sweep runs in the background right after login to reconcile
        // payment logs never confirmed to RMS. It is outside any user journey — the user did not
        // ask for it and cannot act on the result — so no gateway or server status is worth a
        // blocking dialog. The isOffline branch above still fires for this URL, and that is
        // intentional: its 'ApplicationOffline' message is accurate and the whole app is
        // unusable in that state. The gateway timeout is raised upstream of the Lambda, so the
        // server's never-throw boundary cannot suppress it; this has to be handled here. Remove
        // this guard when ADO-111916 moves the sweep to a scheduled job.
        const isBackgroundSync = request.url.includes('payment/send-payments-to-rms');

        if (isGoogleMapError || isBackgroundSync) {
            if (isBackgroundSync) {
                // The dialog was the only signal this sweep had failed, and the re-thrown error
                // dies in payment.effects (catchError drops it for a bare failure action with no
                // reducer). Log it so suppression applies to the dialog only. showInProd = true —
                // the signal being replaced was customer-visible, so production is exactly where
                // it still has to be observable.
                this.logger.logError(`send-payments-to-rms background sync failed (status ${error.status}): ${error.error.message}`, '', true);
            }
            return;
        }

        // verify-transaction runs after POR Pay took the payment, so a 5xx means only the RMS relay failed;
        // payment.effects shows a pending-confirmation message, and an error dialog here would invite a second payment.
        const isVerifyTransaction = request.url.includes('/verify-transaction');
        if (isVerifyTransaction && error.status >= 500) {
            this.logger.logError(`verify-transaction relay failed (status ${error.status}): ${error.error?.message}`, '', true);
            return;
        }

        const isAuthError = [401, 403].includes(error.status);
        const isLivelinkEndpoint = request.url.includes('/livelink');

        // calloffrent's 403 on an ownership-check denial (CALL_OFF_OWNERSHIP_DENIED) is an
        // authorization decision about a specific request, not a session/auth failure — the route
        // handles its own error UI (see close-item-out.component.ts / service-request.component.ts),
        // so it must not also trigger the global auto-logout this interceptor applies to 401/403.
        // The tracking route's 403 (NOT_LICENSED, from LicenseGuard) is likewise an entitlement
        // decision, not a session failure — logging the renter out or showing SessionUnavailable
        // over a lapsed/stale license is wrong, so it self-handles via track.service's error arm.
        const isSelfHandledAuthUrl = request.url.includes('calloffrent') || request.url.includes('/tracking');

        // 401 from GPS livelink endpoint — show "devices unavailable" dialog
        if (isAuthError && isLivelinkEndpoint) {
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('DevicesUnavailable')
            };
            this.showDialog(dialogConfig, true);
        }
        // Handle 401 Unauthorized & 403 Forbidden - logout or show dialog (WI 105319)
        else if ((isAuthError || (!error?.status && isRefreshTokenError)) && !isSQSError && !isExcludedUrl && !isSelfHandledAuthUrl && !this.versionToggle.isQuickLinkVersion()) {
            this.errorService.setError(ErrorType.AuthFailed);
            this.auth.logout();
        } else if (isAuthError && !isSelfHandledAuthUrl && this.versionToggle.isQuickLinkVersion()) {
            // QL mode: show "session unavailable" dialog instead of auto-reloading (WI 105319)
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('SessionUnavailable')
            };
            this.showDialog(dialogConfig, false);
        }
        // 503 from CP — show "service temporarily unavailable" dialog (WI 105319)
        else if (is503 && !isAiImageGenerate) {
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('ServiceTemporarilyUnavailable')
            };
            this.showDialog(dialogConfig, false);
        }
        // Handle SQS errors (502/504 or SQS in message)
        else if (isSQSError && !isAiImageGenerate && !isEsignRequest) {
            dialogConfig = {
                ...dialogConfig,
                errormessage: this.translateService.instant('SQSIssue'),
                apiMessage: errorMessage
            };
            this.showDialog(dialogConfig, this.versionToggle.isQuickLinkVersion() ? false : true);
        }
        // Handle 400 Bad Request - show dialog with close button
        else if (isBadRequestError && !isExcludedUrl && !isQuoteStepsIncomplete) {
            dialogConfig = {
                ...dialogConfig,
                apiMessage: error.error.message || this.translateService.instant('BadRequest')
            };
            this.showDialog(dialogConfig, true);
        }
        // Handle 429 Too Many Requests — show localized rate-limited dialog with close button.
        else if (isRateLimited && !isExcludedUrl && !isSelfHandled429Url) {
            this.showDialog(dialogConfig, true);
        }
        // Handle other errors (404, 500, etc.) - show dialog
        else if (isInternalError && !isExcludedUrl) {
            this.showDialog(dialogConfig);
        }
    }

    private errorHandlingAlerts(status: number, apiMessage: string) {
        let message = '';
        switch (status) {
            case 400:
            case 401:
            case 403:
            case 404:
                message = apiMessage;
                break;
            case 429:
                message = this.translateService.instant('RateLimited');
                break;
            case 502:
                message = this.translateService.instant('ApiStatus502');
                break;
            case 503:
                message = this.translateService.instant('ApiStatus503');
                break;
            case 504:
                message = this.translateService.instant('ApiStatus504');
                break;
            default:
                message = this.translateService.instant('DefaultErrorMessage');
                break;
        }

        return message;
    }
}
